import React, { useMemo } from 'react';
import { translate } from '../../Services/translation';
import ProductCardMyOrders from './ui/ProductCardMyOrders';
import { Order } from '../../Types/Order';
import { EditorStringModel } from '../../Types/EditorString';
import SalesAddress from '../../Types/OrderDetailAddress';
import clsx from 'clsx';

interface OrderDetailProps {
  order: Order;
  isConfirmation: boolean;
  confirmationTitle?: string;
  confirmationText?: EditorStringModel;
  confirmationExtraText?: EditorStringModel;
}

const OrderDetail = ({
  order,
  isConfirmation,
  confirmationTitle,
  confirmationText,
  confirmationExtraText,
}: OrderDetailProps) => {
  const deliveryAddress = useMemo(
    () => (order.deliveries?.length ? order.deliveries[0].address : null),
    [order.deliveries]
  );

  return (
    <div
      className={clsx(
        'container flex flex-col gap-4 bg-gray-100 pb-6 pt-4',
        isConfirmation
          ? ' lg:items-center lg:pb-12 lg:pt-12'
          : 'mx-[-0.8rem] my-0 mb-6 md:mx-[-1.2rem] md:my-0 md:mb-6 lg:m-[unset] lg:items-center'
      )}
    >
      {isConfirmation ? (
        <div className="rounded bg-white p-4 lg:w-full lg:max-w-[660px] lg:p-6">
          <h1 className="mb-2">{confirmationTitle}</h1>
          <div className="flex flex-col gap-2 text-xl lg:flex-row lg:gap-6">
            <span>
              {translate('general.ordernumber')}:
              <span className="font-bold"> {order.externalOrderID}</span>
            </span>
            <span>
              {translate('orderdetail.orderdate')}:
              <span className="font-bold"> {order.orderDate}</span>
            </span>
          </div>
          {confirmationText?.value && (
            <div
              className="mt-2 text-xl"
              dangerouslySetInnerHTML={{
                __html: confirmationText?.value,
              }}
            ></div>
          )}
        </div>
      ) : (
        <div className="rounded bg-white p-4 lg:w-full lg:max-w-[660px] lg:p-6">
          <h1 className="mb-2">
            {translate('general.ordernumber')}: {order.externalOrderID}
          </h1>
          <div className="flex flex-col gap-2 text-xl lg:flex-row lg:gap-6">
            <span>
              {translate('orderdetail.orderdate')}:
              <span className="font-bold"> {order.orderDate}</span>
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col rounded bg-white p-0 lg:w-full lg:max-w-[660px] ">
        {order.orderRows
          .slice()
          .sort((a, b) => a.articleNumber.localeCompare(b.articleNumber))
          .map((product) => (
            <ProductCardMyOrders
              order={order}
              product={product}
              key={product.deliveryId}
            />
          ))}
        <div className="p-4 text-xl lg:p-6 [&_div:not(:last-child)]:mb-2">
          <div className="flex justify-between">
            <span>{translate('checkout.order.sum')}</span>
            <span>{order.orderTotal} {order.currencyCode}</span>
          </div>
          {!order.isBusinessCustomer && (
            <div className="flex justify-between">
              <span>{translate('checkout.order.shipping')}</span>
              <span>{order.orderTotalDeliveryCost} {order.currencyCode}</span>
            </div>
          )}
          <div className="text-bold flex justify-between">
            <span>{translate('orderdetail.grandtotal')}</span>
            <span>{order.orderGrandTotal} {order.currencyCode}</span>
          </div>
          <div className="flex justify-between">
            <span>{translate('checkout.order.vat')}</span>
            <span>{order.orderTotalVat} {order.currencyCode}</span>
          </div>
        </div>
      </div>
      {isConfirmation && confirmationExtraText?.value && (
        <div
          className="rounded bg-white p-4 text-xl lg:w-full lg:max-w-[660px] lg:p-6"
          dangerouslySetInnerHTML={{
            __html: confirmationExtraText?.value,
          }}
        ></div>
      )}
    </div>
  );
};

export default OrderDetail;
